'use client';

import { useState, useEffect } from 'react';
import { Share2, Copy, Check, MessageCircle } from 'lucide-react';

interface MobileFloatingBarProps {
    url: string;
    title: string;
}

export default function MobileFloatingBar({ url, title }: MobileFloatingBarProps) {
    const [copied, setCopied] = useState(false);
    const [mounted, setMounted] = useState(false);
    // Track scroll direction? User requested "always visible", so we won't hide on scroll.

    useEffect(() => {
        setMounted(true);
    }, []);

    if (!mounted) return null;

    const handleNativeShare = async () => {
        if (typeof navigator !== 'undefined' && navigator.share) {
            try {
                await navigator.share({
                    title: title,
                    text: title,
                    url: url
                });
            } catch (err) {
                if ((err as Error).name !== 'AbortError') console.error('Share failed:', err);
            }
        } else {
            // Fallback to copy if native share fails/unavailable
            handleCopy();
        }
    };

    const handleWhatsApp = () => {
        const whatsappUrl = `https://wa.me/?text=${encodeURIComponent(title + ' - ' + url)}`;
        window.open(whatsappUrl, '_blank');
    };

    const handleCopy = async () => {
        try {
            await navigator.clipboard.writeText(url);
            setCopied(true);
            setTimeout(() => setCopied(false), 2000);
        } catch (err) {
            console.error('Clipboard failed', err);
        }
    };

    return (
        <div className="fixed bottom-8 left-0 right-0 z-[100] flex justify-center lg:hidden pointer-events-none">
            <div
                className="bg-slate-900/80 backdrop-blur-xl border border-white/10 shadow-2xl rounded-full px-6 py-3 flex items-center gap-6 pointer-events-auto animate-fade-in-up"
                style={{ animationDuration: '400ms' }}
            >
                {/* Native Share (Primary) */}
                <button
                    onClick={handleNativeShare}
                    className="flex flex-col items-center gap-1 group"
                >
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-active:scale-95 transition-transform">
                        <Share2 className="w-5 h-5 text-white" />
                    </div>
                </button>

                {/* Divider */}
                <div className="w-px h-8 bg-white/10" />

                {/* WhatsApp */}
                <button
                    onClick={handleWhatsApp}
                    className="flex flex-col items-center gap-1 group"
                >
                    <div className="w-10 h-10 rounded-full bg-[#25D366]/20 flex items-center justify-center group-active:scale-95 transition-transform">
                        <MessageCircle className="w-5 h-5 text-[#25D366]" />
                    </div>
                </button>

                {/* Divider */}
                <div className="w-px h-8 bg-white/10" />

                {/* Copy Link */}
                <button
                    onClick={handleCopy}
                    className="flex flex-col items-center gap-1 group"
                >
                    <div className="w-10 h-10 rounded-full bg-white/10 flex items-center justify-center group-active:scale-95 transition-transform relative">
                        {copied ? (
                            <div className="absolute inset-0 flex items-center justify-center animate-fade-in-up" style={{ animationDuration: '300ms' }}>
                                <Check className="w-5 h-5 text-green-400" />
                            </div>
                        ) : (
                            <div className="absolute inset-0 flex items-center justify-center animate-fade-in-up" style={{ animationDuration: '300ms' }}>
                                <Copy className="w-5 h-5 text-slate-300" />
                            </div>
                        )}
                    </div>
                </button>
            </div>
        </div>
    );
}
